import type {
  MentionItem,
  MentionSearchCandidate,
  MentionSearchEnvelope,
  MentionSearchResponse,
} from 'api/mentions/types';

const ROLE_BY_KIND: Record<
  MentionSearchCandidate['kind'],
  MentionItem['role']
> = {
  person: 'user',
  company: 'company',
  page: undefined,
};

/**
 * A LinkedIn member's public profile URL, built from the one piece of it we
 * are given.
 *
 * A member id (`FhptNFuGYa`) is opaque and addresses nothing on the public
 * web; `vanityName` — which the server sends as `handle` — IS the `/in/`
 * slug, so it is the only thing that can produce a working link. Without
 * this, a person mention stored `urn:li:person:<id>` with no URL beside it,
 * and the preview's LinkedIn branch (which matches only `company:` and
 * `urn:organization:`) fell through to an empty id and rendered
 * `https://www.linkedin.com/company/` — a dead link on every person mention.
 *
 * Percent-encoded because vanity names are not ASCII: a real one on dev6 is
 * `eva-ondrašáková-6a3280193`. The raw form would still match the preview's
 * URL regex, but encoding is what makes the href itself correct.
 *
 * Built here rather than sent by the server because the server already sends
 * everything needed, and `handle` is the same field Instagram and X use as a
 * label — only LinkedIn people have a profile URL shape at all.
 */
function linkedInProfileUrl(handle: string | null): string | undefined {
  return handle
    ? `https://www.linkedin.com/in/${encodeURIComponent(handle)}`
    : undefined;
}

export function mapMentionSearchResponse(
  response: MentionSearchResponse,
): MentionSearchEnvelope {
  return {
    items: response.candidates.map((candidate) => ({
      id: candidate.id,
      name: candidate.name,
      image: candidate.avatarUrl,
      kind: candidate.kind,
      role: ROLE_BY_KIND[candidate.kind],
      description: candidate.subtitle ?? undefined,
      isVerified: candidate.verified,
      isDemo: false,
      handle: candidate.handle,
      // `kind === 'person'` stands in for "this is LinkedIn", and that is an
      // invariant rather than a coincidence worth relying on silently: of the
      // four networks, only `LinkedInMentionStrategy` ever emits
      // `MentionCandidateKind::Person` — Facebook and Instagram emit `page`,
      // X emits `page`, and company results are `company` everywhere. If a
      // fifth network ever classifies someone as a person, it would inherit a
      // `linkedin.com/in/` URL built from its own handle, so that network has
      // to revisit this line.
      link:
        candidate.kind === 'person'
          ? linkedInProfileUrl(candidate.handle)
          : undefined,
    })),
    notice: response.notice,
    // `?? null` for the same reason as `refusal` below.
    partialReason: response.partialReason ?? null,
    // `?? null` because an older server build has no such field at all, and
    // `undefined` would read as "not refused" only by accident.
    refusal: response.refusal ?? null,
    failure: response.failure,
  };
}
